import { useEffect, useMemo, useState, type FormEvent } from "react";
import { PRODUCT_LIST, PRODUCTS, RETRACTATION_DAYS } from "@shared/products";
import type { ProductKey } from "@shared/schema";
import { formatDate, formatEUR, todayISO } from "@/lib/utils";

interface CloseSaleFormProps {
  defaultProduct?: ProductKey | null;
  /** YYYY-MM-DD ; par défaut aujourd'hui. Le délai de rétractation part de cette date. */
  defaultClosedAt?: string;
  onSubmit: (values: {
    product: ProductKey;
    totalAmount: number;
    commissionRate: number;
    closedAt: string;
    paymentPlan: number;
    notes: string;
  }) => Promise<void> | void;
  formId?: string;
}

export function CloseSaleForm({
  defaultProduct,
  defaultClosedAt,
  onSubmit,
  formId = "close-sale-form",
}: CloseSaleFormProps) {
  const initialProduct: ProductKey = defaultProduct ?? "power_coaching";
  const [product, setProduct] = useState<ProductKey>(initialProduct);
  const [totalAmount, setTotalAmount] = useState<number>(
    PRODUCTS[initialProduct].defaultPrice,
  );
  const [commissionRate, setCommissionRate] = useState<number>(
    PRODUCTS[initialProduct].defaultCommissionRate,
  );
  const [closedAt, setClosedAt] = useState<string>(defaultClosedAt ?? todayISO());
  const [paymentPlan, setPaymentPlan] = useState<number>(1);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTotalAmount(PRODUCTS[product].defaultPrice);
    setCommissionRate(PRODUCTS[product].defaultCommissionRate);
  }, [product]);

  const retractationEnd = useMemo(() => {
    const d = new Date(closedAt + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + RETRACTATION_DAYS);
    return d.toISOString().slice(0, 10);
  }, [closedAt]);

  const installmentAmount = useMemo(
    () => Math.round((totalAmount / Math.max(1, paymentPlan)) * 100) / 100,
    [totalAmount, paymentPlan],
  );

  const totalCommission = useMemo(
    () => Math.round(totalAmount * commissionRate * 100) / 100,
    [totalAmount, commissionRate],
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (totalAmount <= 0) {
      setError("Montant invalide");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        product,
        totalAmount,
        commissionRate,
        closedAt,
        paymentPlan: Math.max(1, paymentPlan),
        notes,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form id={formId} onSubmit={submit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Produit">
          <select
            className="input-base"
            value={product}
            onChange={(e) => setProduct(e.target.value as ProductKey)}
          >
            {PRODUCT_LIST.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date de close">
          <input
            type="date"
            className="input-base"
            value={closedAt}
            onChange={(e) => setClosedAt(e.target.value)}
            required
          />
        </Field>
        <Field label="Montant total (€)">
          <input
            type="number"
            min={0}
            step="0.01"
            className="input-base tabular"
            value={totalAmount}
            onChange={(e) => setTotalAmount(Number(e.target.value))}
            required
          />
        </Field>
        <Field label="Plan de paiement (x mensualités)">
          <input
            type="number"
            min={1}
            max={36}
            className="input-base tabular"
            value={paymentPlan}
            onChange={(e) =>
              setPaymentPlan(Math.max(1, Math.floor(Number(e.target.value))))
            }
            required
          />
        </Field>
        <Field label="Taux commission">
          <div className="relative">
            <input
              type="number"
              min={0}
              max={100}
              step="0.5"
              className="input-base tabular pr-8"
              value={Math.round(commissionRate * 1000) / 10}
              onChange={(e) =>
                setCommissionRate(Math.max(0, Number(e.target.value)) / 100)
              }
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] text-sm">
              %
            </span>
          </div>
        </Field>
        <Field label="Notes (facultatif)">
          <input
            className="input-base"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Mode paiement, accords, etc."
          />
        </Field>
      </div>

      <div className="bg-[var(--color-navy-50)] border border-[var(--color-navy-100)] rounded-[var(--radius-md)] p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Mensualité">{formatEUR(installmentAmount)}</Stat>
        <Stat label="Commission totale">
          <span className="text-[var(--color-coral-600)] font-semibold">
            {formatEUR(totalCommission)}
          </span>
        </Stat>
        <Stat label="Fin rétractation">{formatDate(retractationEnd)}</Stat>
        <Stat label="Échéances">{paymentPlan}×</Stat>
      </div>

      {error && (
        <p className="text-sm text-[var(--color-coral-600)]">{error}</p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          className="btn btn-success"
          disabled={submitting}
        >
          {submitting ? "Enregistrement…" : "Enregistrer le close"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--color-muted-foreground)] mb-1">
        {label}
      </div>
      <div className="font-semibold text-[var(--color-navy-700)] tabular">
        {children}
      </div>
    </div>
  );
}
